import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { buildBreadcrumbLd } from "@/lib/seo";
import { GUIDES, GUIDE_CATEGORIES, readingTime } from "@/data/guides";
import { useSeoT, GUIDES_I18N, PAGE_META } from "@/lib/seoPagesI18n";

const LOCALES = { fr: "fr-CH", de: "de-CH", en: "en-GB" };

export default function GuidesIndex() {
  const { lang, t: st } = useSeoT();
  const g18 = GUIDES_I18N[lang] || {};
  const loc = (guide) => (lang !== "fr" ? { ...guide, ...(g18[guide.slug] || {}) } : guide);
  const catLabel = (id) => (st.guides.cats?.[id]) || GUIDE_CATEGORIES.find((c) => c.id === id)?.label;
  const fmtDate = (d) => new Date(d).toLocaleDateString(LOCALES[lang] || "fr-CH", { day: "numeric", month: "long", year: "numeric" });

  const [cat, setCat] = useState("all");
  const bc = [{ name: st.bc.home, path: "/" }, { name: st.bc.guides, path: "/guides" }];
  const featured = GUIDES.filter((x) => x.featured);
  const list = cat === "all" ? GUIDES : GUIDES.filter((x) => x.category === cat);

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="guides-index">
      <Seo title={(PAGE_META[lang] || PAGE_META.fr).guides.title} description={(PAGE_META[lang] || PAGE_META.fr).guides.desc} path="/guides" jsonLd={buildBreadcrumbLd(toLdItems(bc))} />
      <Header />
      <section className="relative bg-[#0A0F2C] text-white overflow-hidden pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.4), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#FFD84D] font-bold mb-5"><BookOpen size={12} /> {st.guides.badge}</span>
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {st.guides.h1a} <span className="text-[#FFD84D]">{st.guides.h1b}</span>
          </h1>
          <p className="mt-5 text-white/80 text-base md:text-lg max-w-2xl">{st.guides.heroSub}</p>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
          <div className="mx-auto" style={{ maxWidth: "1280px" }}>
            <h2 className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{st.guides.featured}</h2>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
              {featured.map((base) => {
                const g = loc(base);
                return (
                  <Link key={g.slug} to={`/guides/${g.slug}`} className="group relative overflow-hidden rounded-3xl bg-[#F7F7F8] hover:bg-[#EAF0FF] p-8 transition-colors" data-testid={`guide-featured-${g.slug}`}>
                    <div className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${g.heroTone === "dark" ? "bg-[#0A0F2C] text-white" : "bg-[#2D5BFF] text-white"}`}>{catLabel(g.category)}</div>
                    <h3 className="mt-4 font-extrabold text-2xl leading-tight group-hover:text-[#2D5BFF] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{g.title}</h3>
                    <p className="mt-3 text-[#4B5563] leading-relaxed">{g.excerpt}</p>
                    <div className="mt-5 flex items-center gap-3 text-xs text-[#6B7280]"><Clock size={13} /> {readingTime(base)} {st.guides.min} · {fmtDate(g.date)}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="pb-16 md:pb-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => setCat("all")} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${cat === "all" ? "bg-[#0A0F2C] text-white" : "bg-[#F7F7F8] text-[#0A0F2C] hover:bg-gray-200"}`} data-testid="guides-filter-all">{st.guides.all}</button>
            {GUIDE_CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${cat === c.id ? "bg-[#0A0F2C] text-white" : "bg-[#F7F7F8] text-[#0A0F2C] hover:bg-gray-200"}`} data-testid={`guides-filter-${c.id}`}>{catLabel(c.id)}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {list.map((base) => {
              const g = loc(base);
              return (
                <Link key={g.slug} to={`/guides/${g.slug}`} className="group flex flex-col bg-white border border-gray-100 hover:border-[#2D5BFF]/30 hover:shadow-lg rounded-2xl overflow-hidden transition-all" data-testid={`guide-card-${g.slug}`}>
                  <div className={`h-2 ${g.heroTone === "dark" ? "bg-[#0A0F2C]" : "bg-[#2D5BFF]"}`} />
                  <div className="p-6 flex flex-col flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">{catLabel(g.category)}</div>
                    <h3 className="mt-2 font-extrabold text-lg leading-snug group-hover:text-[#2D5BFF] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{g.title}</h3>
                    <p className="mt-2 text-sm text-[#4B5563] leading-relaxed flex-1">{g.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-[#6B7280]">
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {readingTime(base)} {st.guides.min}</span>
                      <span className="inline-flex items-center gap-1 font-bold text-[#2D5BFF]">{st.guides.read} <ArrowRight size={12} /></span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
